'use client';

import { ParsedUrlQueryInput } from 'querystring';

import { useRouter } from 'next/navigation';
import { FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import * as Dialog from '@/components/Dialog';
import { usePageTextContent } from '@/context/pageTextContent';
import { resolvePath } from '@/utils/helpers';
import { Paths } from '@/utils/paths';

import { useSearchPageStore } from '../../useSearchPageStore';

import { CategoriesSection } from './CategoriesSection';
import { IncludedNetworkSection } from './IncludedNetworkSection';
import { NearInput } from './NearInput';

export type FiltersModalProps = {
  categories: Category[];
};

export function FiltersModal({ categories }: FiltersModalProps) {
  const [searchNear, setSearchNear] = useState('');
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [areNetworksIncluded, setAreNetworksIncluded] = useState(true);

  const { content } = usePageTextContent<SearchPageContent>();

  const { filtersTitle, clearFiltersButton, applyFiltersButton } = content;

  const isFiltersModalOpen = useSearchPageStore(
    state => state.isFiltersModalOpen,
  );
  const onCloseFiltersModal = useSearchPageStore(
    state => state.onCloseFiltersModal,
  );

  const includeNetworks = useSearchPageStore(state => state.includeNetworks);
  const onIncludeNetworksChange = useSearchPageStore(
    state => state.onIncludeNetworksChange,
  );

  const near = useSearchPageStore(state => state.near);
  const onNearChange = useSearchPageStore(state => state.onNearChange);

  const selectedCategories = useSearchPageStore(
    state => state.selectedCategories,
  );
  const onSelectedCategoriesChange = useSearchPageStore(
    state => state.onSelectedCategoriesChange,
  );

  const setIsLoading = useSearchPageStore(state => state.setIsLoading);

  useEffect(() => {
    if (!isFiltersModalOpen) return;

    setSearchNear(near);
    setFilterCategories(selectedCategories);
    setAreNetworksIncluded(includeNetworks);
  }, [isFiltersModalOpen]);

  const router = useRouter();

  const onSearchNearChange = (value: string) => {
    setSearchNear(value);
  };

  const onCategoryChange = (categoryId: string) => {
    if (filterCategories.includes(categoryId)) {
      const _filterCategories = filterCategories.filter(
        category => category !== categoryId,
      );

      setFilterCategories(_filterCategories);

      return;
    }

    const _filterCategories = [...filterCategories, categoryId];

    setFilterCategories(_filterCategories);
  };

  const isCategoryChecked = (categoryId: string) => {
    return filterCategories.includes(categoryId);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const categoriesParam = filterCategories.join(',');

    const query: ParsedUrlQueryInput = { page: 1 };

    if (searchNear) query.near = searchNear;

    if (categoriesParam) query.categoryId = categoriesParam;

    if (areNetworksIncluded !== includeNetworks) {
      query.includeNetworks = areNetworksIncluded;
    }

    onSelectedCategoriesChange(filterCategories);
    onNearChange(searchNear);
    onIncludeNetworksChange(areNetworksIncluded);

    setIsLoading(true);

    const pageUrl = resolvePath({ pathname: Paths.SEARCH, query });

    router.replace(pageUrl);

    onCloseFiltersModal();
  };

  const onAreNetworksIncludedChange = (value: boolean) => {
    setAreNetworksIncluded(value);
  };

  const onClearFilters = () => {
    setSearchNear('');
    onNearChange('');

    setFilterCategories([]);
    onSelectedCategoriesChange([]);

    onIncludeNetworksChange(true);

    setIsLoading(true);

    const pageUrl = resolvePath({ pathname: Paths.SEARCH, query: { page: 1 } });

    router.replace(pageUrl);

    onCloseFiltersModal();
  };

  return (
    <Dialog.Root open={isFiltersModalOpen} onOpenChange={onCloseFiltersModal}>
      <Dialog.Portal>
        <Dialog.Overlay />

        <Dialog.Content aria-describedby="Filters modal">
          <Dialog.Title className="sr-only">{filtersTitle}</Dialog.Title>

          <Dialog.ContentHeader className="flex items-center rounded-t-none py-4 md:rounded-t-md">
            <div className="flex w-full justify-center">
              <h2 className="text-xl leading-6 font-normal">{filtersTitle}</h2>
            </div>

            <Dialog.Close className="right-4 left-auto" />
          </Dialog.ContentHeader>

          <Dialog.ContentBody>
            <form id="filters-form" onSubmit={onSubmit} className="space-y-6">
              <IncludedNetworkSection
                isIncluded={areNetworksIncluded}
                onChange={onAreNetworksIncludedChange}
              />

              <NearInput onChange={onSearchNearChange} value={searchNear} />

              <CategoriesSection
                onChange={onCategoryChange}
                categories={categories}
                isChecked={isCategoryChecked}
              />
            </form>
          </Dialog.ContentBody>

          <div className="flex w-full items-center rounded-b-none p-4 md:rounded-b-md">
            <div className="flex w-full justify-between">
              <Button
                variant="link"
                className="underline"
                onClick={onClearFilters}
                type="button"
              >
                {clearFiltersButton}
              </Button>

              <Button variant="zmyle" type="submit" form="filters-form">
                {applyFiltersButton}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
